import { Select } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';

type CityOption = { value: string; label: string };

type Props = {
    value: number | null | undefined;
    onChange: (cityId: number | null) => void;
    label?: string;
    placeholder?: string;
    /** Label for the currently selected city when it is not in search results. */
    initialLabel?: string | null;
    disabled?: boolean;
    clearable?: boolean;
    error?: ReactNode;
};

export const CitySearchSelect = ({
    value,
    onChange,
    label,
    placeholder,
    initialLabel,
    disabled,
    clearable = true,
    error,
}: Props) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [options, setOptions] = useState<CityOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cities.query({
                    page: 1,
                    limit: 15,
                    searchText: debouncedSearch.trim() || undefined,
                    // API `@IsNotEmpty()` rejects `{}`; null field keeps where non-empty.
                    where: { readyForRoutell: null } as unknown as Record<string, never>,
                });

                if (cancelled) return;

                setOptions(
                    (data?.items || []).map((city) => ({
                        value: String(city.id),
                        label: city.title || `#${city.id}`,
                    })),
                );
            } catch (err) {
                if (!cancelled) {
                    setOptions([]);
                    // Soft-fail: keep the select usable; parent can still show a toast if needed.
                    console.warn(apiErrorMessage(err));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [debouncedSearch]);

    const data = useMemo(() => {
        const list = [...options];
        const selected = value != null ? String(value) : null;

        if (selected && !list.some((o) => o.value === selected)) {
            list.unshift({
                value: selected,
                label: initialLabel || `#${selected}`,
            });
        }

        return list;
    }, [options, value, initialLabel]);

    return (
        <Select
            label={label ?? t('editors.citySearchSelect.label')}
            placeholder={placeholder ?? t('editors.citySearchSelect.placeholder')}
            data={data}
            value={value != null ? String(value) : null}
            onChange={(next) => onChange(next ? Number(next) : null)}
            searchable
            searchValue={search}
            onSearchChange={setSearch}
            clearable={clearable}
            disabled={disabled}
            error={error}
            nothingFoundMessage={
                loading
                    ? t('editors.citySearchSelect.searching')
                    : t('editors.citySearchSelect.noCitiesFound')
            }
        />
    );
};
